import { ArrowPathIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Component, type ErrorInfo, type ReactNode } from 'react';
import * as Sentry from '@sentry/react';

export interface AppErrorBoundaryProps {
  children: ReactNode;
  /** Fallback personnalisé (optionnel) */
  fallback?: ReactNode;
}

interface AppErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Error boundary modulaire : capture les erreurs React dans une zone isolée,
 * log en console, affiche un fallback minimal et permet de réinitialiser l'état (recharger le module).
 */
export class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  constructor(props: AppErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<AppErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('AppErrorBoundary caught an error:', error, errorInfo);
    Sentry.captureException(error, { extra: { ...errorInfo } });
    this.setState({ errorInfo });
  }

  private resetState = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render(): ReactNode {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return (
        <div className="flex flex-col items-center justify-center p-6 text-center">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />
          </div>
          <p className="mt-3 text-sm font-medium text-gray-900">Une erreur s&apos;est produite</p>
          <p className="mt-1 text-xs text-gray-500">Ce module a rencontré un problème.</p>
          <button
            type="button"
            onClick={this.resetState}
            className="mt-4 inline-flex items-center gap-2 rounded-md border border-transparent bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
          >
            <ArrowPathIcon className="h-4 w-4" />
            Recharger le module
          </button>
          {import.meta.env.DEV && this.state.error?.message && (
            <details className="mt-4 w-full max-w-md">
              <summary className="cursor-pointer text-xs text-gray-500">Détails (dev)</summary>
              <pre className="mt-1 overflow-auto rounded bg-gray-100 p-2 text-left text-xs text-gray-700">
                {this.state.error.message}
              </pre>
            </details>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}
